import { Injectable } from '@angular/core';
import { ApiOperation, ApiParameter } from './api-discoverability.service';
import { ConfigService } from '../../shared/services/config.service';

@Injectable({
  providedIn: 'root'
})
export class DynamicOpenApiService {
  constructor(private configService: ConfigService) {}

  /**
   * Generate a dynamic OpenAPI 3.0 specification for a single API operation
   */
  generateOpenApiSpec(operation: ApiOperation): any {
    const spec = {
      openapi: '3.0.0',
      info: {
        title: `${operation.applicationName} - ${operation.name}`,
        //description: operation.description,
        version: operation.version || '1.0.0'
      },
       servers: this.generateServers(operation),
      security: [
        {
          oauth2Profiles: []
        },
        {
          subscriptionKeyHeader: []
        }
      ],
      paths: {
        [`/${operation.path}`]: {
          [operation.method.toLowerCase()]: {
            summary: operation.name,
            description: operation.description,
            operationId: operation.id,
            tags: [operation.applicationName],
            parameters: this.generateParameters(operation.parameters),
            responses: this.generateResponses(),
            ...(this.requiresRequestBody(operation.method) && {
              requestBody: this.generateRequestBody()
            })
          }
        }
      },
      components: {
        schemas: {
          ApiResponse: {
            type: 'object',
            properties: {
              data: {
                type: 'object',
                description: 'Response data'
              },
              message: {
                type: 'string',
                description: 'Response message'
              }
            }
          },
          ErrorResponse: {
            type: 'object',
            properties: {
              error: {
                type: 'string',
                description: 'Error message'
              },
              code: {
                type: 'integer',
                description: 'Error code'
              }
            }
          }
        }
      }
    };

    return spec;
  }

  /**
   * Generate server configurations based on application name and environment
   */
  private generateServers(operation: ApiOperation): any[] {
    const config = this.configService.getConfig();
    // Get servers for the specific application or use default
    let baseUrl = config.apiMeshConfig?.baseUrl;
    let subscriptionKey = config.apiMeshConfig?.subscriptionKey;

    if(!baseUrl || !subscriptionKey) {
        console.error('API Mesh configuration is missing in the configuration settings.');        
    }

    const servers = [
      {
        url: baseUrl,
        description: `${operation.applicationName}  through API Mesh`,
        ['x-subscriptionKey']: subscriptionKey
      }
    ];

    return servers;
  }

  /**
   * Generate OpenAPI parameters from API operation parameters
   */
  private generateParameters(parameters: ApiParameter[]): any[] {
    if (!parameters || parameters.length === 0) {
      return [];
    }

    return parameters.map(param => ({
      name: param.name,
      in: 'query',
      required: param.required,
      schema: {
        type: this.mapParameterType(param.type)
      },
      description: `${param.name} parameter`,
      example: this.generateExampleValue(param.type)
    }));
  }

  /**
   * Map parameter types to OpenAPI schema types
   */
  private mapParameterType(type: string): string {
    const typeMap: { [key: string]: string } = {
      'string': 'string',
      'int': 'integer',
      'integer': 'integer',
      'number': 'number',
      'bool': 'boolean',
      'boolean': 'boolean',
      'date': 'string',
      'datetime': 'string'
    };

    return typeMap[type.toLowerCase()] || 'string';
  }

  /**
   * Generate example values for parameters
   */
  private generateExampleValue(type: string): any {
    const examples: { [key: string]: any } = {
      'string': '',
      'integer': 123,
      'number': 123.45,
      'boolean': true,
      'date': '2023-12-01',
      'datetime': '2023-12-01T10:30:00Z'
    };

    return examples[type.toLowerCase()] || '';
  }

  /**
   * Check if the HTTP method requires a request body
   */
  private requiresRequestBody(method: string): boolean {
    const methodsWithBody = ['post', 'put', 'patch'];
    return methodsWithBody.includes(method.toLowerCase());
  }

  /**
   * Generate request body schema for methods that require it
   */
  private generateRequestBody(): any {
    return {
      required: true,
      content: {
        'application/json': {
          schema: {
            type: 'object',
            properties: {
              data: {
                type: 'object',
                description: 'Request payload data'
              }
            },
            example: {
              data: {
                key: 'value',
                property: 'example'
              }
            }
          }
        }
      }
    };
  }

  /**
   * Generate standard responses for the operation
   */
  private generateResponses(): any {
    return {
      '200': {
        description: 'Successful operation',
        content: {
          'application/json': {
            schema: {
              $ref: '#/components/schemas/ApiResponse'
            }
          }
        }
      },
      '400': {
        description: 'Bad request',
        content: {
          'application/json': {
            schema: {
              $ref: '#/components/schemas/ErrorResponse'
            }
          }
        }
      },
      '401': {
        description: 'Unauthorized',
        content: {
          'application/json': {
            schema: {
              $ref: '#/components/schemas/ErrorResponse'
            }
          }
        }
      },
      '404': {
        description: 'Not found',
        content: {
          'application/json': {
            schema: {
              $ref: '#/components/schemas/ErrorResponse'
            }
          }
        }
      },
      '500': {
        description: 'Internal server error',
        content: {
          'application/json': {
            schema: {
              $ref: '#/components/schemas/ErrorResponse'
            }
          }
        }
      }
    };
  }

  /**
   * Get the subscription key for a specific server URL
   */
  getSubscriptionKeyForServer(operation: ApiOperation, serverUrl: string): string | null {
    const servers = this.generateServers(operation);
    const server = servers.find(s => s.url === serverUrl);
    return server?.subscriptionKey || null;
  }

  /**
   * Generate a serializable specification that can be passed via router
   */
  generateSerializableSpec(operation: ApiOperation): string {
    const spec = this.generateOpenApiSpec(operation);
    return JSON.stringify(spec);
  }
}